import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
    IconButton,
    Divider,
    FormControlLabel,
    Checkbox,
    Link,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Snackbar,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip
} from '@mui/material';
import axios from 'axios';
import { Gor } from '../types';
// Import API config to set axios defaults
import '../config/api';
import { renderAddressWithMapLink } from '../utils/addressUtils';
import { Helmet } from 'react-helmet-async';
import { isSuperAdminEmail, isAdminOrSuperAdmin } from '../config/adminEmails';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import TelegramIcon from '@mui/icons-material/Telegram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BookingInfo, CalendarEvent } from '../types';
import { getHariFromTanggal } from '../utils/dateUtils';

const CoachingClinicBooking: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [gor, setGor] = useState<Gor | null>(location.state?.gor || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const lastBooking = localStorage.getItem('lastBooking');
    const defaultNama = lastBooking ? JSON.parse(lastBooking).nama : localStorage.getItem('nama') || '';
    const defaultLevel = lastBooking ? JSON.parse(lastBooking).level : localStorage.getItem('levelBermain') || 'Beginner';
    const defaultNomorHP = lastBooking ? JSON.parse(lastBooking).nomorHP : localStorage.getItem('nomorHP') || '';
    const [formData, setFormData] = useState({
        tanggal: '',
        nama: defaultNama || '',
        levelBermain: defaultLevel || '',
        email: user?.email || '',
        nomorHP: defaultNomorHP || ''
    });
    const [bookingInfo, setBookingInfo] = useState<BookingInfo>({
        tanggal: null,
        jumlahPemain: 0,
        daftarPemain: [],
        pemainByLevel: {},
        kapasitasMaksimal: 0,
        daftarWaitinglist: [],
        waitinglistByLevel: {},
        daftarWaitinglistDetail: [],
        jumlahWaitinglist: 0
    });
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
    const [paymentRetryCount, setPaymentRetryCount] = useState(0);
    const [showPaymentErrorDialog, setShowPaymentErrorDialog] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    const [localBooking, setLocalBooking] = useState<{ nama: string; level: string } | null>(null);
    const [showCalendarDialog, setShowCalendarDialog] = useState(false);
    const [calendarEvent, setCalendarEvent] = useState<CalendarEvent | null>(null);
    const [showBlockedLevelDialog, setShowBlockedLevelDialog] = useState(false);
    const [blockedLevelMsg, setBlockedLevelMsg] = useState("");
    const [showKapasitasDialog, setShowKapasitasDialog] = useState(false);
    const [agreeToTerms, setAgreeToTerms] = useState(true);
    const [bookingSubmitted, setBookingSubmitted] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [editDialog, setEditDialog] = useState<{open: boolean, player: string, level: string, statusBayar: string, levelList: string[], playerEmail?: string, idBooking?: string} | null>(null);
    const [editField, setEditField] = useState<{nama?: string, level?: string, statusBayar?: string}>({});
    const [editLoading, setEditLoading] = useState(false);
    const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);

    const LEVEL_ORDER = ['Prelevel', 'Beginner', 'Intermediate', 'Advanced'];

    // Helper function untuk handle form changes dan reset bookingSubmitted
    const handleFormChange = (field: string, value: string) => {
        if (bookingSubmitted) {
            setBookingSubmitted(false); // Reset booking submitted state ketika user mengubah form
        }
        setFormData({ ...formData, [field]: value });
    };

    // Helper function untuk reset form setelah booking selesai
    const resetFormForNewBooking = () => {
        setBookingSubmitted(false);
        setError(null);
        setFormData(prev => ({
            ...prev,
            tanggal: '',
            nomorHP: ''
        }));
    };

    const fetchBookingNames = useCallback(async () => {
        if (!id) return;
        try {
            setLoadingBookings(true);
            const response = await axios.get<BookingInfo>(`/api/coaching/${id}/bookings`);
            setBookingInfo(response.data);
        } catch (error) {
            console.error('Error fetching booking names:', error);
        } finally {
            setLoadingBookings(false);
        }
    }, [id]);

    const fetchGorData = async () => {
        try {
            setLoading(true);
            setError(null);
            if (location.state?.gor) {
                setGor(location.state.gor);
                setLoading(false);
                return;
            }
            if (!id) {
                setError('ID GOR tidak valid');
                setLoading(false);
                return;
            }
            const response = await axios.get<Gor>(`/api/coaching/${id}`);
            if (response.data) {
                setGor(response.data);
            } else {
                setError('Data GOR tidak ditemukan');
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Gagal memuat data GOR');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGorData();
        fetchBookingNames();
        setBookingSubmitted(false); // Reset booking submitted state ketika component mount atau route berubah

        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser?.email) {
            setFormData(prev => ({
                ...prev,
                email: currentUser.email
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.state]);

    // Auto-refresh booking data when window regains focus (user returns from Midtrans payment)
    useEffect(() => {
        const handleWindowFocus = () => {
            fetchBookingNames();
        };

        window.addEventListener('focus', handleWindowFocus);
        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [fetchBookingNames]);

    useEffect(() => {
        if (bookingInfo && bookingInfo.tanggal) {
            localStorage.setItem(`jumlahPemain_${id}_${bookingInfo.tanggal}`, bookingInfo.jumlahPemain.toString());
            if (bookingInfo.kapasitasMaksimal !== undefined) {
                localStorage.setItem(`kapasitasMaksimal_${id}_${bookingInfo.tanggal}`, bookingInfo.kapasitasMaksimal.toString());
            }
        }
    }, [bookingInfo, id]);

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Jika booking sudah berhasil disubmit, jangan tampilkan dialog konfirmasi lagi
        if (bookingSubmitted) {
            console.log('Booking sudah disubmit, tidak membuka dialog konfirmasi');
            return;
        }
        
        // Cek status blokir akun terlebih dahulu
        if (formData.email) {
            try {
                const response = await axios.get(`/api/coaching?action=check-blocked&email=${encodeURIComponent(formData.email)}`);
                if (response.data.isBlocked) {
                    setError('Maaf, akun Anda telah diblokir dan tidak dapat melakukan booking coaching clinic. Silakan hubungi admin untuk informasi lebih lanjut.');
                    return;
                }
            } catch (error) {
                console.error('Error checking blocked status:', error);
                // Lanjutkan booking jika error (fallback untuk availability)
            }
        }
        
        const minimal = gor?.levelMinimal || 'Prelevel';
        const userLevelIdx = LEVEL_ORDER.indexOf(formData.levelBermain);
        const minimalIdx = LEVEL_ORDER.indexOf(minimal);
        if (minimalIdx > -1 && userLevelIdx > -1 && userLevelIdx < minimalIdx) {
            setBlockedLevelMsg(`Maaf, level minimal untuk booking di GOR ini adalah ${minimal}. Silakan pilih GOR lain atau naikkan level Anda.`);
            setShowBlockedLevelDialog(true);
            return;
        }
        // Hapus pengecekan kapasitas penuh karena sekarang boleh booking untuk waitinglist
        // Ketika kapasitas penuh, booking akan otomatis masuk ke waitinglist
        // Reset booking submitted state dan clear error sebelum membuka dialog konfirmasi
        setBookingSubmitted(false);
        setError(null); // Clear error sebelumnya
        setLocalBooking({ nama: formData.nama.trim(), level: formData.levelBermain });
        setShowConfirmationDialog(true);
    };

    const handleConfirmBooking = async () => {
        setPendingSubmit(true);
        setSuccess(false);
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (!currentUser?.email) {
            setShowConfirmationDialog(false);
            setShowLoginDialog(true);
            setPendingSubmit(false);
            return;
        }
        setError(null);
        // Trim nama untuk menghilangkan whitespace di depan dan belakang
        const trimmedNama = formData.nama.trim();
        
        if (!formData.tanggal || !trimmedNama || !formData.levelBermain || !formData.nomorHP) {
            setError('Semua field harus diisi');
            setLoading(false);
            setPendingSubmit(false);
            return;
        }
        setLoading(true);
        
        // Jika member 1 bulan, booking semua tanggal lalu lanjutkan ke dialog pembayaran
        if (formData.tanggal === "__MEMBER_1_BULAN__") {
            try {
                // Generate unique order_id untuk semua bookings member
                const memberOrderId = `MEMBER-COACH-${Date.now()}`;
                // Set payment order ID sebelum booking
                setPaymentOrderId(memberOrderId);
                for (const tanggal of gor?.tanggalTersedia || []) {
                    const bookingData = {
                        tanggal,
                        tempat: gor?.nama,
                        nama: trimmedNama,
                        levelBermain: formData.levelBermain,
                        email: currentUser.email,
                        nomorHP: formData.nomorHP,
                        gross_amount: gor?.biaya ? parseInt(gor.biaya) : 100000,
                        order_id: memberOrderId
                    };
                    const response = await axios.post(`/api/coaching`, bookingData);
                    // Cek apakah response sukses untuk setiap booking
                    if (response.data.message !== 'Booking coaching berhasil') {
                        throw new Error(response.data.message || 'Gagal booking untuk tanggal tertentu');
                    }
                    // Order ID received in response
                }
                setBookingSubmitted(true); // Set flag bahwa booking sudah berhasil disubmit
                setShowConfirmationDialog(false);
                localStorage.setItem('lastBooking', JSON.stringify({
                    nama: trimmedNama,
                    level: formData.levelBermain,
                    nomorHP: formData.nomorHP
                }));
                // Start Midtrans payment flow dengan memberOrderId
                setTimeout(() => {
                    startMidtransPayment(memberOrderId);
                }, 300);
            } catch (err: any) {
                // Reset paymentOrderId jika error
                setPaymentOrderId(null);
                setError(err.message || 'Gagal booking member 1 bulan. Silakan coba lagi.');
                setShowErrorDialog(true);
            } finally {
                setLoading(false);
                setPendingSubmit(false);
            }
            return;
        }
        
        const bookingData = {
            tanggal: formData.tanggal,
            tempat: gor?.nama,
            nama: trimmedNama,
            levelBermain: formData.levelBermain,
            email: currentUser.email,
            nomorHP: formData.nomorHP,
            gross_amount: gor?.biaya ? parseInt(gor.biaya) : 100000
        };
        try {
            const response = await axios.post(`/api/coaching`, bookingData);
            if (response.data.message === 'Booking coaching berhasil') {
                setSuccess(true);
                setBookingSubmitted(true); // Set flag bahwa booking sudah berhasil disubmit
                await fetchGorData();
                setShowConfirmationDialog(false);
                localStorage.setItem('lastBooking', JSON.stringify({
                  nama: trimmedNama,
                  level: formData.levelBermain,
                  nomorHP: formData.nomorHP
                }));
                if (response.data.order_id) setPaymentOrderId(response.data.order_id);
                else {
                    // Fallback jika order_id tidak ada di response
                    setPaymentOrderId(`COAC-${Date.now()}`);
                }
                const tanggalStr = formData.tanggal;
                const jamStr = gor?.jam || '19.00-21.00';
                let start = new Date();
                let end = new Date();
                try {
                    const [tgl, bulan, tahun] = tanggalStr.split(' ');
                    const bulanMap: { [key: string]: number } = {
                        'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
                        'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11
                    };
                    const jamRange = jamStr.split('-');
                    const [jamMulai, menitMulai] = jamRange[0].split('.')
                    const [jamSelesai, menitSelesai] = (jamRange[1] || '21.00').split('.')
                    start = new Date(parseInt(tahun), bulanMap[bulan as keyof typeof bulanMap], parseInt(tgl), parseInt(jamMulai), parseInt(menitMulai || '0'));
                    end = new Date(parseInt(tahun), bulanMap[bulan as keyof typeof bulanMap], parseInt(tgl), parseInt(jamSelesai), parseInt(menitSelesai || '0'));
                } catch {}
                setCalendarEvent({
                    title: `Booking Coaching Clinic di ${gor?.nama}`,
                    description: `Coaching Clinic di ${gor?.nama} bersama RaketerS. Level: ${formData.levelBermain}`,
                    location: gor?.alamat || '',
                    start,
                    end
                });
                setShowCalendarDialog(true); // Langsung tampilkan dialog kalender
                fetchBookingNames();
                // ABIS BOOKING SUKSES, TRIGGER RELOAD FLOATING STATUS
                window.dispatchEvent(new CustomEvent('booking-updated'));
            } else {
                setError(response.data.message || 'Gagal melakukan booking. Silakan coba lagi.');
                // Tetap tutup dialog konfirmasi dan tampilkan dialog error
                setShowConfirmationDialog(false);
                setShowErrorDialog(true);
            }
        } catch (error: any) {
            setError(error.response?.data?.message || 'Gagal melakukan booking. Silakan coba lagi.');
            // Tetap tutup dialog konfirmasi dan tampilkan dialog error
            setShowConfirmationDialog(false);
            setShowErrorDialog(true);
        } finally {
            setLoading(false);
            setPendingSubmit(false);
        }
    };

    const handleCloseConfirmation = () => {
        setShowCancelDialog(true);
    };

    const handleCancelBooking = () => {
        setShowConfirmationDialog(false);
        setShowCancelDialog(false);
    };

    const handleLoginClick = () => {
        setShowLoginDialog(false);
        navigate('/login');
    };

    const handleEditSubmit = async () => {
        if (!gor || !bookingInfo.tanggal || !editDialog) return;
        setEditLoading(true);
        try {
            // Edit nama
            if (editField.nama && editField.nama !== editDialog.player) {
                await axios.post('/api/bookings?action=coaching-edit', {
                    tanggal: bookingInfo.tanggal,
                    tempat: gor.nama,
                    nama: editDialog.player,
                    email: editDialog.playerEmail || '',
                    field: 'nama',
                    value: editField.nama
                });
            }
            // Edit level
            if (editField.level && editField.level !== editDialog.level) {
                await axios.post('/api/bookings?action=coaching-edit', {
                    tanggal: bookingInfo.tanggal,
                    tempat: gor.nama,
                    nama: editField.nama || editDialog.player,
                    email: editDialog.playerEmail || '',
                    field: 'level',
                    value: editField.level
                });
            }
            // Edit status bayar
            if (typeof editField.statusBayar === 'string' && editField.statusBayar.length > 0) {
                await axios.post('/api/bookings?action=coaching-edit', {
                    tanggal: bookingInfo.tanggal,
                    tempat: gor.nama,
                    nama: editField.nama || editDialog.player,
                    email: editDialog.playerEmail || '',
                    field: 'statusBayar',
                    value: editField.statusBayar
                });
            }
            setEditDialog(null);
            fetchBookingNames();
            fetchGorData();
        } catch (e) {
            alert('Gagal update. Pastikan Anda punya akses.');
        } finally {
            setEditLoading(false);
        }
    };

    const handleAddToCalendar = () => {
        if (!calendarEvent) return;
        const pad = (n: number) => n.toString().padStart(2, '0');
        const formatDate = (d: Date) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
        const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${calendarEvent.title}\nDESCRIPTION:${calendarEvent.description}\nLOCATION:${calendarEvent.location}\nDTSTART:${formatDate(calendarEvent.start)}\nDTEND:${formatDate(calendarEvent.end)}\nEND:VEVENT\nEND:VCALENDAR`;
        const blob = new Blob([ics], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'booking-coaching.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setShowCalendarDialog(false);
        // Reset form untuk booking baru after adding to calendar
        resetFormForNewBooking();
        // Start Midtrans payment flow (fallback to QR dialog handled inside)
        startMidtransPayment();
    };

    const handleSkipCalendar = () => {
        setShowCalendarDialog(false);
        // Reset form untuk booking baru after skipping
        resetFormForNewBooking();
        // Start Midtrans payment flow
        startMidtransPayment();
    };

    // Trigger pembayaran Midtrans (QRIS dinamis) setelah booking sukses
    const startMidtransPayment = async (overrideOrderId?: string, isRetry: boolean = false) => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const baseAmount =
                formData.tanggal === "__MEMBER_1_BULAN__"
                    ? (gor?.biaya && gor.biaya !== '-' ? parseInt(gor.biaya) * (gor?.tanggalTersedia?.length || 4) : 100000 * (gor?.tanggalTersedia?.length || 4))
                    : (gor?.biaya && gor.biaya !== '-' ? parseInt(gor.biaya) : 100000);

            if (!currentUser?.email) {
                // Jika tidak ada email (belum login), tampilkan pesan error
                alert('Silakan login terlebih dahulu untuk melakukan pembayaran');
                return;
            }

            const orderId = overrideOrderId || paymentOrderId || `COAC-${Date.now()}`;

            const response = await axios.post('/api/payment/snap-token', {
                order_id: orderId,
                gross_amount: baseAmount,
                nama: formData.nama || currentUser?.displayName || 'Pemain',
                email: currentUser.email,
                finish_redirect_url: `${window.location.origin}/booking-sukses`
            });

            const redirectUrl = response.data?.redirect_url;
            if (redirectUrl) {
                // Reset retry count jika berhasil
                setPaymentRetryCount(0);
                window.location.href = redirectUrl;
            } else {
                // Jika gagal dapat URL, coba lagi atau tampilkan error dialog
                handlePaymentFailure();
            }
        } catch (err) {
            console.error('Gagal memulai pembayaran Midtrans (coaching):', err);
            handlePaymentFailure();
        }
    };

    const handlePaymentFailure = () => {
        const newRetryCount = paymentRetryCount + 1;
        setPaymentRetryCount(newRetryCount);
        setPaymentOrderId(null); // Reset order ID untuk attempt baru

        if (newRetryCount < 2) {
            // Coba lagi otomatis setelah 2 detik
            setTimeout(() => {
                startMidtransPayment(undefined, true);
            }, 2000);
        } else {
            // Sudah coba 2 kali, tampilkan dialog error
            setShowPaymentErrorDialog(true);
        }
    };

    const handleRetryPayment = () => {
        setShowPaymentErrorDialog(false);
        setPaymentRetryCount(0);
        startMidtransPayment();
    };

    if (loading) {
        return (
            <>
                <Helmet>
                    <title>Detail Coaching Clinic - RaketerS</title>
                </Helmet>
                <Container>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                        <CircularProgress />
                    </Box>
                </Container>
            </>
        );
    }

    if (!gor) {
        return (
            <>
                <Helmet>
                    <title>Detail Coaching Clinic - RaketerS</title>
                </Helmet>
                <Container>
                    <Alert severity="error">Data GOR tidak ditemukan</Alert>
                </Container>
            </>
        );
    }

    const allLevels: string[] = Array.from(new Set([
        ...Object.keys(bookingInfo.pemainByLevel || {}),
        localBooking?.level || formData.levelBermain
    ]));

    // Superadmin: akses penuh (edit pemain, hapus booking, dll)
    const isSuperAdmin = user?.email ? isSuperAdminEmail(user.email) : false;
    const isAdmin = user?.email ? isAdminOrSuperAdmin(user.email) : false;
    // Admin: akses terbatas (hanya edit live score dan lihat warna hijau pembayaran)
    // Admin atau Superadmin: bisa lihat warna berbeda untuk yang sudah bayar/belum
    // (Superadmin punya semua akses admin, jadi juga bisa lihat warna hijau)
    
    // Daftar level lengkap untuk Coaching
    const LEVELS = ['Prelevel','Beginner','Intermediate','Advanced'];

    const bayarDiLapanganNameSet = new Set(
        (bookingInfo.daftarPemainBayarDiLapangan || [])
            .map(nama => nama?.trim().toLowerCase())
            .filter(Boolean)
    );
    const filteredPemainByLevel = isAdmin
        ? Object.entries(bookingInfo.pemainByLevel || {}).reduce<Record<string, string[]>>((acc, [level, pemainList]) => {
            const filtered = pemainList.filter(nama => !bayarDiLapanganNameSet.has(nama?.trim().toLowerCase()));
            if (filtered.length > 0) acc[level] = filtered;
            return acc;
        }, {})
        : (bookingInfo.pemainByLevel || {});

    const generateShareText = () => {
        if (!gor || !bookingInfo.tanggal) return '';

        // Buat teks informasi booking
        let shareText = `🏸 *Info Coaching Clinic Badminton RaketerS*\n\n`;
        const totalListedPlayers = Number(bookingInfo.jumlahPemain || 0) + Number(bookingInfo.jumlahWaitinglist || 0);
        shareText += `*${gor.nama}*\n`;
        shareText += `📅 ${getHariFromTanggal(bookingInfo.tanggal)}, ${bookingInfo.tanggal}\n`;
        if (gor.jam && gor.jam !== '-') {
            shareText += `⏰ Jam Main: ${gor.jam}\n`;
        }
        shareText += `📍 ${gor.alamat}\n\n`;
        shareText += `👥 *Daftar Pemain* (${totalListedPlayers}/${bookingInfo.kapasitasMaksimal ?? gor.kapasitasMaksimal})\n`;
        shareText += `✅ *Pemain utama sudah pasti berhak mengikuti coaching (sudah bayar)*\n\n`;

        // Tambahkan daftar pemain per level
        Object.entries(bookingInfo.pemainByLevel || {}).forEach(([level, pemainList]) => {
            if (pemainList.length > 0) {
                shareText += `*Level ${level}* (${pemainList.length} pemain):\n`;
                pemainList.forEach((nama, idx) => {
                    shareText += `${idx + 1}. ${nama}\n`;
                });
                shareText += `\n`;
            }
        });

        // Tambahkan waitinglist jika ada
        if (bookingInfo.jumlahWaitinglist && bookingInfo.jumlahWaitinglist > 0) {
            shareText += `⏳ *Reservasi (Menunggu Pembayaran)* (${bookingInfo.jumlahWaitinglist} pemain):\n`;
            shareText += `💡 *Catatan:* Pemain reservasi masih berhak mengikuti coaching jika ada slot kosong setelah konfirmasi pembayaran.\n\n`;
            Object.entries(bookingInfo.waitinglistByLevel || {}).forEach(([level, pemainList]) => {
                if (pemainList.length > 0) {
                    shareText += `Level ${level}: ${pemainList.join(', ')}\n`;
                }
            });
            shareText += `\n`;
        }

        if (gor.biaya && gor.biaya !== '-') {
            shareText += `💰 Biaya: Rp${parseInt(gor.biaya).toLocaleString('id-ID')}\n`;
        }

        // Tambahkan link ke Coaching Clinic di akhir
        const gorId = id || gor?.id;
        if (gorId) {
            const gorUrl = `${window.location.origin}/coaching/${gorId}`;
            shareText += `\n\nDaftar: 🔗 ${gorUrl}`;
        }
        return shareText;
    };

    const handleShare = () => {
        if (!gor || !bookingInfo.tanggal) {
            setError('Tidak ada informasi booking untuk dibagikan');
            return;
        }
        setShowShareDialog(true);
    };

    const shareToWhatsApp = () => {
        const shareText = generateShareText();
        const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const shareToTwitter = () => {
        const shareText = generateShareText();
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const shareToFacebook = () => {
        const shareText = generateShareText();
        const currentUrl = window.location.href;
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const shareToTelegram = () => {
        const shareText = generateShareText();
        const currentUrl = window.location.href;
        const url = `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
        setShowShareDialog(false);
    };

    const copyToClipboard = async () => {
        const shareText = generateShareText();
        try {
            await navigator.clipboard.writeText(shareText);
            setShareSnackbarOpen(true);
            setShowShareDialog(false);
        } catch (err) {
            // Fallback untuk browser lama
            const textArea = document.createElement('textarea');
            textArea.value = shareText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setShareSnackbarOpen(true);
                setShowShareDialog(false);
            } catch (e) {
                setError('Gagal menyalin teks. Silakan salin manual.');
            }
            document.body.removeChild(textArea);
        }
    };

    return (
        <>
            <Helmet>
                <title>{gor.nama} - RaketerS</title>
            </Helmet>

            <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
                <Grid container spacing={isMobile ? 2 : 4}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                            p: isMobile ? 2 : 3,
                            background: theme.palette.mode === 'dark' 
                                ? (isMobile ? '#0a1626' : 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)')
                                : undefined,
                            border: theme.palette.mode === 'dark' 
                                ? '1px solid rgba(99,170,255,0.22)'
                                : undefined,
                            borderRadius: 3
                        }}>
                            <Typography 
                                variant={isMobile ? "h5" : "h4"} 
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                {gor?.nama}
                            </Typography>
                            <div style={{ position: 'relative', width: '100%' }}>
                              <img
                                src={gor?.gambar}
                                alt={gor?.nama}
                                style={{ 
                                    width: '100%', 
                                    height: 'auto', 
                                    marginBottom: isMobile ? '0.5rem' : '1rem',
                                    borderRadius: '8px'
                                }}
                              />
                            </div>
                            {gor?.jam && gor.jam !== '-' && (
                                <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ color: 'success.main', fontWeight: 700, mb: 1 }}>
                                    Jam Main: {gor.jam}
                                </Typography>
                            )}
                            <Box>
  {gor?.deskripsi && gor.deskripsi.length > 50 ? (
    showFullDescription ? (
      <>
        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          paragraph
          sx={{ whiteSpace: 'pre-line', mb: 0.5 }}
        >
          {gor.deskripsi}
        </Typography>
        <Button
          size="small"
          onClick={() => setShowFullDescription(false)}
          sx={{
            textTransform: 'none',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            fontWeight:'bold',
            mb: 1,
            color: '#42a5f5',
            background: 'transparent',
            minWidth:'unset',
            p:0
          }}
        >
          Show less
        </Button>
      </>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          paragraph
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mb: 0,
            flex: 1,
            pr: 1
          }}
        >
          {gor.deskripsi}
        </Typography>
        <Button
          size="small"
          onClick={() => setShowFullDescription(true)}
          sx={{
            textTransform: 'none', fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight:'bold', minWidth: 'unset',
            color: '#42a5f5',
            p: 0, ml: 1, background: 'transparent'
          }}
        >
          Show more
        </Button>
      </Box>
    )
  ) : (
    <Typography variant={isMobile ? 'body2' : 'body1'} paragraph>{gor?.deskripsi}</Typography>
  )
}</Box>
                            <Typography variant={isMobile ? "body2" : "body1"}>
                                <strong>Alamat:</strong> {renderAddressWithMapLink(gor?.alamat)}
                            </Typography>
                            {gor?.biaya && gor.biaya !== '-' && (
                                <Typography variant={isMobile ? "body2" : "body1"} sx={{ color: 'primary.main', fontWeight: 700 }}>
                                    Biaya Coaching: Rp{parseInt(gor.biaya).toLocaleString('id-ID')}
                                </Typography>
                            )}
                            {gor?.levelMinimal && (
                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    Level Minimal: {gor.levelMinimal}
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                Pemain Utama: {bookingInfo.jumlahPemain}/{bookingInfo.kapasitasMaksimal ?? gor.kapasitasMaksimal}
                                {(bookingInfo.jumlahWaitinglist ?? 0) > 0 && (
                                    <span style={{ marginLeft: '8px', color: '#ff9800' }}>
                                        • Reservasi: {bookingInfo.jumlahWaitinglist}
                                    </span>
                                )}
                            </Typography>
                            {/* Tombol Share - muncul ketika ada info booking */}
                            {bookingInfo.tanggal && (
                                <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Tooltip title="Bagikan info booking terkini">
                                        <Button
                                            onClick={handleShare}
                                            variant="outlined"
                                            color="primary"
                                            size={isMobile ? "small" : "medium"}
                                            startIcon={<ShareIcon />}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            Bagikan Info Booking
                                        </Button>
                                    </Tooltip>
                                </Box>
                            )}

                            {bookingInfo.daftarPemain.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Coaching Clinic {bookingInfo.tanggal ? `${getHariFromTanggal(bookingInfo.tanggal)}, ${bookingInfo.tanggal}` : ''} ({bookingInfo.jumlahPemain} pemain):
                                    </Typography>
                                    <Typography variant="body2" color="success.main" sx={{ mb: 2, fontStyle: 'italic', fontWeight: 600 }}>
                                        ✅ Pemain yang terdaftar di sini sudah pasti berhak mengikuti coaching (sudah melakukan pembayaran).
                                    </Typography>
                                    {loadingBookings ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            {Object.entries(filteredPemainByLevel).map(([level, pemainList]) => (
                                                <Box key={level} sx={{ mb: 2 }}>
                                                    <Typography 
                                                        variant={isMobile ? "body2" : "body1"} 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            mb: 1,
                                                            color: 'primary.main',
                                                            borderBottom: '2px solid',
                                                            borderColor: 'primary.main',
                                                            pb: 0.5
                                                        }}
                                                    >
                                                        Level {level} ({pemainList.length} pemain)
                                                    </Typography>
                                                    <Box sx={{ 
                                                        display: 'grid',
                                                        gridTemplateColumns: isMobile
                                                            ? 'repeat(2, 1fr)'
                                                            : pemainList.length >= 3
                                                                ? 'repeat(3, 1fr)'
                                                                : 'repeat(2, 1fr)',
                                                        gap: 1
                                                    }}>
                                                        {pemainList.map((nama, index) => {
                                                            // Hitung posisi global waitinglist berdasarkan daftarWaitinglistDetail atau daftarWaitinglist
                                                            let posisiLabel = '';
                                                            try {
                                                                const pemainDetail = bookingInfo.daftarWaitinglistDetail?.find(p => p.nama?.trim() === nama?.trim() && p.level?.trim() === level?.trim());
                                                                const idBooking = pemainDetail?.idBooking || '';
                                                                if (Array.isArray(bookingInfo.daftarWaitinglistDetail) && bookingInfo.daftarWaitinglistDetail.length > 0) {
                                                                    const foundIdx = bookingInfo.daftarWaitinglistDetail.findIndex(p => {
                                                                        if (p.idBooking && idBooking) return p.idBooking.toString().trim() === idBooking.toString().trim();
                                                                        return (p.nama || '').toString().trim() === (nama || '').toString().trim() && (p.level || '').toString().trim() === (level || '').toString().trim();
                                                                    });
                                                                    if (foundIdx !== -1) posisiLabel = `${foundIdx + 1}. `;
                                                                } else if (Array.isArray(bookingInfo.daftarWaitinglist)) {
                                                                    const foundIdx = bookingInfo.daftarWaitinglist.findIndex(n => (n || '').toString().trim() === (nama || '').toString().trim());
                                                                    if (foundIdx !== -1) posisiLabel = `${foundIdx + 1}. `;
                                                                }
                                                            } catch (e) {
                                                                posisiLabel = '';
                                                            }
                                                            return (
                                                            <Box
                                                                key={index}
                                                                sx={{
                                                                    backgroundColor: 'primary.light',
                                                                    color: 'primary.contrastText',
                                                                    padding: '8px 12px',
                                                                    borderRadius: '6px',
                                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                                    fontWeight: 500,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    cursor: isSuperAdmin ? 'pointer' : 'default'
                                                                }}
                                                                onClick={() => {
                                                                    if (!isSuperAdmin) return;
                                                                    // Cari detail pemain dari daftarPemainDetail berdasarkan nama dan level
                                                                    const pemainDetail = bookingInfo.daftarPemainDetail?.find(p => 
                                                                        p.nama?.trim() === nama?.trim() && 
                                                                        p.level?.trim() === level?.trim()
                                                                    );
                                                                    const playerEmail = pemainDetail?.email || '';
                                                                    const idBooking = pemainDetail?.idBooking || '';
                                                                    const statusBayar = pemainDetail?.statusBayarText || (pemainDetail?.statusBayar ? 'Sudah bayar' : 'Belum bayar');
                                                                    setEditDialog({ open: true, player: nama, level: String(level), statusBayar, levelList: LEVELS, playerEmail, idBooking });
                                                                    setEditField({ nama, level: String(level), statusBayar });
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: 'primary.contrastText',
                                                                        flexShrink: 0
                                                                    }}
                                                                />
                                                                {`${posisiLabel}${nama}`}
                                                            </Box>
                                                        )})}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {isAdmin && (bookingInfo.daftarPemainBayarDiLapangan?.length ?? 0) > 0 && (
                                <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.action.hover, borderRadius: 2 }}>
                                    <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold', mb: 1, color: 'secondary.main' }}>
                                        Daftar Pemain Bayar di Lapang ({bookingInfo.daftarPemainBayarDiLapangan?.length} pemain):
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                        Daftar khusus ini hanya ditampilkan untuk Admin/Superadmin.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {(bookingInfo.daftarPemainBayarDiLapangan || []).map((nama, idx) => {
                                            const pemainDetail = bookingInfo.daftarPemainDetail?.find(p => p.nama?.trim() === nama?.trim());
                                            return (
                                                <Chip
                                                    key={pemainDetail?.idBooking || `${nama}-${idx}`}
                                                    label={nama}
                                                    size="small"
                                                    clickable={!!(isSuperAdmin && pemainDetail?.idBooking)}
                                                    onClick={isSuperAdmin && pemainDetail?.idBooking ? () => {
                                                        const statusBayar = pemainDetail.statusBayarText || 'Bayar di lapang';
                                                        setEditDialog({ open: true, player: nama, level: pemainDetail.level || '', statusBayar, levelList: LEVELS, playerEmail: pemainDetail.email, idBooking: pemainDetail.idBooking });
                                                        setEditField({ nama, level: pemainDetail.level || '', statusBayar });
                                                    } : undefined}
                                                    sx={{
                                                        backgroundColor: theme.palette.secondary.light,
                                                        color: theme.palette.secondary.contrastText,
                                                        fontWeight: 600,
                                                        cursor: isSuperAdmin && pemainDetail?.idBooking ? 'pointer' : 'default'
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}

                            {/* Daftar Waitinglist - Selalu tampilkan judul */}
                            {bookingInfo.tanggal && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant={isMobile ? "body2" : "body1"} sx={{ fontWeight: 'bold', mb: 1, color: 'warning.main' }}>
                                        Daftar Reservasi (Menunggu Pembayaran) {bookingInfo.tanggal ? `${getHariFromTanggal(bookingInfo.tanggal)}, ${bookingInfo.tanggal}` : ''} ({(bookingInfo.jumlahWaitinglist ?? 0)} pemain):
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                        💡 Pemain reservasi masih berhak mengikuti coaching jika ada slot kosong setelah konfirmasi pembayaran.
                                    </Typography>
                                    {loadingBookings ? (
                                        <CircularProgress size={20} />
                                    ) : (bookingInfo.daftarWaitinglist && bookingInfo.daftarWaitinglist.length > 0) ? (
                                        <Box sx={{ mt: 2 }}>
                                            {Object.entries(bookingInfo.waitinglistByLevel || {}).map(([level, pemainList]) => (
                                                <Box key={level} sx={{ mb: 2 }}>
                                                    <Typography 
                                                        variant={isMobile ? "body2" : "body1"} 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            mb: 1,
                                                            color: 'warning.main',
                                                            borderBottom: '2px solid',
                                                            borderColor: 'warning.main',
                                                            pb: 0.5
                                                        }}
                                                    >
                                                        Level {level} ({pemainList.length} pemain)
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {pemainList.map((nama, idx) => {
                                                            const isPaid = bookingInfo.paidNames?.includes(nama);
                                                            const isAdmin = user?.email ? isAdminOrSuperAdmin(user.email) : false;
                                                            const pemainDetail = bookingInfo.daftarWaitinglistDetail?.find(p => p.nama === nama);
                                                            // Hitung posisi global waitinglist
                                                            let posisiLabel = '';
                                                            try {
                                                                const idBooking = pemainDetail?.idBooking || '';
                                                                if (Array.isArray(bookingInfo.daftarWaitinglistDetail) && bookingInfo.daftarWaitinglistDetail.length > 0) {
                                                                    const foundIdx = bookingInfo.daftarWaitinglistDetail.findIndex(p => {
                                                                        if (p.idBooking && idBooking) return p.idBooking.toString().trim() === idBooking.toString().trim();
                                                                        return (p.nama || '').toString().trim() === (nama || '').toString().trim() && (p.level || '').toString().trim() === (level || '').toString().trim();
                                                                    });
                                                                    if (foundIdx !== -1) posisiLabel = `${foundIdx + 1}. `;
                                                                } else if (Array.isArray(bookingInfo.daftarWaitinglist)) {
                                                                    const foundIdx = bookingInfo.daftarWaitinglist.findIndex(n => (n || '').toString().trim() === (nama || '').toString().trim());
                                                                    if (foundIdx !== -1) posisiLabel = `${foundIdx + 1}. `;
                                                                }
                                                            } catch (e) {
                                                                posisiLabel = '';
                                                            }
                                                            return (
                                                                <Chip
                                                                    key={idx}
                                                                    label={`${posisiLabel}${nama}`}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: theme.palette.primary.light,
                                                                        color: theme.palette.common.black,
                                                                        fontWeight: 600,
                                                                        cursor: isAdmin ? 'pointer' : 'default',
                                                                        '&:hover': isAdmin ? {
                                                                            backgroundColor: isPaid ? theme.palette.success.main : theme.palette.warning.main,
                                                                            transform: 'scale(1.05)'
                                                                        } : {}
                                                                    }}
                                                                    onClick={isAdmin ? () => {
                                                                        const levelList = ['Prelevel', 'Beginner', 'Intermediate', 'Advanced'];
                                                                        setEditDialog({
                                                                            open: true,
                                                                            player: nama,
                                                                            level: pemainDetail?.level || level,
                                                                            statusBayar: pemainDetail?.statusBayarText || (isPaid ? 'Sudah bayar' : 'Belum bayar'),
                                                                            levelList: levelList,
                                                                            playerEmail: pemainDetail?.email,
                                                                            idBooking: pemainDetail?.idBooking
                                                                        });
                                                                        setEditField({ nama: nama, level: pemainDetail?.level || level, statusBayar: pemainDetail?.statusBayarText || (isPaid ? 'Sudah bayar' : 'Belum bayar') });
                                                                    } : undefined}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                            Belum ada pemain di waitinglist.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {!gor.bisaBooking && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    GOR ini belum tersedia untuk booking
                                </Alert>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                            p: isMobile ? 2 : 3,
                            background: theme.palette.mode === 'dark' 
                                ? (isMobile ? '#0a1626' : 'linear-gradient(135deg, rgba(13,71,161,0.08) 0%, rgba(21,101,192,0.04) 100%)')
                                : undefined,
                            border: theme.palette.mode === 'dark' 
                                ? '1px solid rgba(99,170,255,0.22)'
                                : undefined,
                            borderRadius: 3
                        }}>
                            <Typography 
                                variant={isMobile ? "h6" : "h5"} 
                                gutterBottom
                                sx={{ fontWeight: 'bold' }}
                            >
                                Booking Coaching Clinic
                            </Typography>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                    {error === 'Pemain Utama sudah penuh, tidak dapat booking lagi.' ? (
                                        <b>Pemain Utama sudah penuh! Anda tidak dapat booking untuk tanggal ini.</b>
                                    ) : error}
                                </Alert>
                            )}
                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Booking berhasil!
                                </Alert>
                            )}
                            <form onSubmit={handleFormSubmit}>
                                <Grid container spacing={isMobile ? 1 : 2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Tanggal"
                                            value={formData.tanggal}
                                            onChange={(e) => handleFormChange('tanggal', e.target.value)}
                                            required
                                            disabled={!gor.bisaBooking}
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            <MenuItem value="__MEMBER_1_BULAN__">
                                                Member 1 Bulan (4x Main)
                                            </MenuItem>
                                            {gor.tanggalTersedia?.map((tanggal) => (
                                                <MenuItem key={tanggal} value={tanggal}>
                                                    {getHariFromTanggal(tanggal)}, {tanggal}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nama"
                                            value={formData.nama}
                                            onChange={(e) => handleFormChange('nama', e.target.value)}
                                            margin="normal"
                                            required
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Level Bermain"
                                            value={formData.levelBermain}
                                            onChange={(e) => handleFormChange('levelBermain', e.target.value)}
                                            margin="normal"
                                            required
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            <MenuItem value="Prelevel">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Prelevel</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Baru bermain badminton, kurang paham dengan aturan badminton dan bagaimana cara memukul.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Beginner">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Beginner</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Bisa tepakan, tidak mengerti rotasi, belum bisa mengarahkan bola, kadang sering loss ketika mukul, belum mengerti cara mengeluarkan tenaga, footstep berantakan, tidak bisa backhand.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Intermediate">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Intermediate</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Cukup mengerti rotasi, bisa mengarahkan bola, jarang terjadi loss ketika mukul, mengerti cara mengeluarkan tenaga, tapi footstep masih kurang rapi, backhand kurang lancar.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="Advanced">
                                                <Box>
                                                    <Typography sx={{ fontWeight: 700 }}>Advanced</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{
                                                        display: 'block',
                                                        maxWidth: isMobile ? 260 : 400,
                                                        whiteSpace: 'normal',
                                                        overflow: 'visible',
                                                        textOverflow: 'unset'
                                                    }}>
                                                        Mengerti rotasi, bisa mengarahkan bola dan akurat, tidak pernah loss ketika mukul, bisa mengatur pengeluaran tenaga, footstep rapi, backhand lancar dan akurat.
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            type="email"
                                            value={formData.email}
                                            disabled
                                            margin="normal"
                                            required
                                            size={isMobile ? "small" : "medium"}
                                            helperText="Email akan terisi otomatis dari akun Google Anda"
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                                                },
                                                '& .MuiFormHelperText-root': {
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    marginTop: 0.5
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nomor HP"
                                            type="tel"
                                            value={formData.nomorHP}
                                            onChange={(e) => handleFormChange('nomorHP', e.target.value)}
                                            margin="normal"
                                            required
                                            size={isMobile ? "small" : "medium"}
                                            placeholder="Contoh: 081234567890"
                                            helperText="Nomor HP untuk konfirmasi booking"
                                            inputProps={{
                                                pattern: "[0-9]*",
                                                inputMode: "numeric"
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={agreeToTerms}
                                                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    Saya setuju dengan{' '}
                                                    <Link
                                                        href="/syarat-ketentuan"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ 
                                                            color: 'primary.main',
                                                            textDecoration: 'none',
                                                            '&:hover': {
                                                                textDecoration: 'underline'
                                                            }
                                                        }}
                                                    >
                                                        Syarat & Ketentuan Coaching Clinic RaketerS
                                                    </Link>
                                                </Typography>
                                            }
                                            sx={{ mt: 1, mb: 1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type={user?.email ? "submit" : "button"}
                                            variant="contained"
                                            color={(bookingInfo.kapasitasMaksimal && bookingInfo.jumlahPemain >= bookingInfo.kapasitasMaksimal) ? "warning" : "primary"}
                                            fullWidth
                                            disabled={loading || !gor.bisaBooking || !agreeToTerms}
                                            size={isMobile ? "large" : "large"}
                                            onClick={!user?.email ? handleLoginClick : undefined}
                                            sx={{ 
                                                py: isMobile ? 1 : 1.5,
                                                mt: isMobile ? 1 : 2
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} /> : (user?.email ? ((bookingInfo.kapasitasMaksimal && bookingInfo.jumlahPemain >= bookingInfo.kapasitasMaksimal) ? 'WAITINGLIST' : 'Submit Booking') : 'Login Untuk Booking')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </Paper>
                    </Grid>
                </Grid>

                <Dialog
                    open={showLoginDialog}
                    onClose={() => setShowLoginDialog(false)}
                    fullScreen={isMobile}
                >
                    <DialogTitle>Login Diperlukan</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Anda perlu login terlebih dahulu untuk melakukan booking.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowLoginDialog(false)}>Batal</Button>
                        <Button onClick={handleLoginClick} variant="contained" color="primary">
                            Login
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showConfirmationDialog && !bookingSubmitted}
                    onClose={handleCloseConfirmation}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                                : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.background.paper} 100%)`,
                            backdropFilter: 'blur(6px)',
                            borderRadius: 4,
                            boxShadow: 8,
                            p: 0
                        }
                    }}
                >
                    <DialogTitle 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-start', 
                            fontWeight: 'bold', 
                            fontSize: { xs: 16, sm: 22 }, 
                            letterSpacing: 1, 
                            pb: 1.5, 
                            borderBottom: '2px solid', 
                            borderColor: 'primary.main', 
                            background: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.92)' : 'rgba(255,255,255,0.7)', 
                            color: theme.palette.text.primary, 
                            backdropFilter: 'blur(2px)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 32, borderRadius: 2, background: 'primary.main', mr: 1 }} />
                            Booking Confirmation
                        </Box>
                        <IconButton onClick={handleCloseConfirmation} size="small" sx={{ color: theme.palette.mode === 'dark' ? 'grey.300' : 'grey.700', marginLeft: 'auto' }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: { xs: 2, sm: 3 }, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(255,255,255,0.85)', borderRadius: 3, mt: 2, color: theme.palette.text.primary }}>
                        <Box sx={{ mb: 2, p: 2, borderRadius: 3, boxShadow: 2, background: theme.palette.mode === 'dark' ? 'rgba(16,28,50,0.95)' : 'rgba(255,255,255,0.95)', color: theme.palette.text.primary }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5, letterSpacing: 1 }}>
                                {gor?.nama}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Tanggal:</Typography>
                                    {formData.tanggal === "__MEMBER_1_BULAN__" ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {gor?.tanggalTersedia?.map((tanggal) => (
                                                <Box key={tanggal} sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: theme.palette.primary.light, color: theme.palette.primary.contrastText, fontWeight: 600 }}>
                                                    {getHariFromTanggal(tanggal)}, {tanggal}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: theme.palette.primary.light, color: theme.palette.primary.contrastText, fontWeight: 600 }}>{formData.tanggal}</Box>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Jam Main:</Typography>
                                    <Box sx={{ px: 1.5, py: 0.5, borderRadius: 2, background: theme.palette.success.light, color: theme.palette.success.contrastText, fontWeight: 600 }}>{gor?.jam}</Box>
                                </Box>
                            </Box>
                        </Box>
                        <Divider sx={{ my: 2, borderColor: 'primary.main', opacity: 0.5, borderBottomWidth: 2 }} />
                        
                        {/* Error display dalam dialog konfirmasi */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', letterSpacing: 0.5 }}>
                                Daftar Pemain per Level
                            </Typography>
                            {allLevels.map((level: string) => {
                                const pemainList = bookingInfo.pemainByLevel[level] || [];
                                const isUserLevel = level === (localBooking?.level || formData.levelBermain);
                                const userInList = pemainList.includes(localBooking?.nama || formData.nama);
                                const displayList = isUserLevel && !userInList && localBooking?.nama
                                    ? [...pemainList, localBooking.nama]
                                    : pemainList;
                                return (
                                    <Box key={level} sx={{ mb: 2, p: 2, borderRadius: 2, background: theme.palette.mode === 'dark' ? 'rgba(50,60,80,0.7)' : 'rgba(230,245,255,0.7)', boxShadow: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark', mb: 1, letterSpacing: 0.5 }}>
                                            Level {level} <span style={{ fontWeight: 400 }}>({displayList.length} pemain)</span>
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                                            {displayList.map((nama, idx) => {
                                                const isUser = nama === (localBooking?.nama || formData.nama) && isUserLevel;
                                                return (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            background: isUser ? '#fff' : theme.palette.primary.light,
                                                            color: isUser ? theme.palette.primary.main : theme.palette.primary.contrastText,
                                                            px: 2,
                                                            py: 0.7,
                                                            borderRadius: 3,
                                                            fontWeight: isUser ? 800 : 500,
                                                            border: isUser ? `2px solid ${theme.palette.primary.main}` : 'none',
                                                            boxShadow: isUser ? 3 : 0,
                                                            fontSize: '1rem',
                                                            letterSpacing: 0.5
                                                        }}
                                                    >
                                                        {nama}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, background: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.75)' : 'rgba(255,255,255,0.7)', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
                        <Button onClick={handleCloseConfirmation} color="inherit" sx={{ fontWeight: 600, borderRadius: 2, px: { xs: 1.5, sm: 3 }, color: theme.palette.text.primary, fontSize: { xs: '0.75rem', sm: '1.1rem' }, minWidth: 0 }}>
                            Batal
                        </Button>
                        <Button onClick={handleConfirmBooking} variant="contained" color="primary" disabled={pendingSubmit} sx={{ fontWeight: 700, borderRadius: 2, px: { xs: 1.5, sm: 3 }, boxShadow: 2, fontSize: { xs: '0.75rem', sm: '1.1rem' }, minWidth: 0 }}>
                            {pendingSubmit ? <CircularProgress size={20} /> : 'Submit Booking'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showCancelDialog}
                    onClose={() => setShowCancelDialog(false)}
                >
                    <DialogTitle>Batalkan Pemesanan?</DialogTitle>
                    <DialogContent>
                        <Typography>Anda yakin ingin membatalkan pemesanan booking ini?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowCancelDialog(false)} color="inherit">Tidak</Button>
                        <Button onClick={handleCancelBooking} color="error">Ya, Batalkan</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showCalendarDialog}
                    onClose={handleSkipCalendar}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
                        Tambahkan Event ke Calendar Anda?
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center', pb: 0 }}>
                        <Typography sx={{ mb: 2 }}>
                            Agar tidak lupa jadwal coaching, tambahkan event booking ini ke kalender Anda!
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button onClick={handleAddToCalendar} variant="contained" color="primary" sx={{ fontWeight: 700, px: 3 }}>
                            Tambahkan ke Kalender
                        </Button>
                        <Button onClick={handleSkipCalendar} color="inherit" sx={{ fontWeight: 600, px: 3 }}>
                            Lewati
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={showBlockedLevelDialog} onClose={() => setShowBlockedLevelDialog(false)}>
                    <DialogTitle>Peringatan</DialogTitle>
                    <DialogContent>
                        <Typography>{blockedLevelMsg}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowBlockedLevelDialog(false)} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={showKapasitasDialog} onClose={() => setShowKapasitasDialog(false)}>
                    <DialogTitle>Pemain Utama Penuh</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {error}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowKapasitasDialog(false)} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>


                <Dialog 
                    open={showErrorDialog} 
                    onClose={() => setShowErrorDialog(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle sx={{ textAlign: 'center', color: 'error.main', fontWeight: 700 }}>
                        ❌ Booking Gagal
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                            Maaf, booking coaching clinic Anda gagal!
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Silakan coba lagi atau hubungi admin jika masalah berlanjut.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <Button 
                            onClick={() => {
                                setShowErrorDialog(false);
                                setError(null);
                            }} 
                            variant="contained" 
                            color="error"
                            sx={{ fontWeight: 700, px: 4 }}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>

            {/* Dialog Edit Booking (Admin) */}
            <Dialog
                open={!!editDialog?.open}
                onClose={() => setEditDialog(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        background: theme.palette.mode === 'dark'
                            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                            : undefined,
                        borderRadius: 3,
                    }
                }}
            >
                <DialogTitle sx={{ background: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.92)' : undefined, color: theme.palette.text.primary, borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : undefined }}>
                    Edit Booking
                </DialogTitle>
                <DialogContent sx={{ background: theme.palette.mode === 'dark' ? theme.palette.background.paper : undefined, color: theme.palette.text.primary }}>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Nama"
                        value={editField.nama || ''}
                        onChange={(e) => setEditField(prev => ({ ...prev, nama: e.target.value }))}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Level</InputLabel>
                        <Select
                            label="Level"
                            value={editField.level || ''}
                            onChange={(e) => setEditField(prev => ({ ...prev, level: String(e.target.value) }))}
                        >
                            {LEVELS.map((lvl) => (
                                <MenuItem key={lvl} value={lvl}>{lvl}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                                        <FormControl fullWidth margin="dense">
                                                <InputLabel>Status Bayar</InputLabel>
                                                <Select
                                                        label="Status Bayar"
                                                        value={editField.statusBayar || 'Belum bayar'}
                                                        onChange={(e) => setEditField(prev => ({ ...prev, statusBayar: e.target.value }))}
                                                >
                                                        <MenuItem value="Sudah bayar">Sudah bayar</MenuItem>
                                                        <MenuItem value="Belum bayar">Belum bayar</MenuItem>
                                                        <MenuItem value="Bayar di lapang">Bayar di lapang</MenuItem>
                                                </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(10,22,38,0.75)' : undefined }}>
                    <Button onClick={() => setEditDialog(null)}>Batal</Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            // Konfirmasi sebelum hapus
                            if (!window.confirm('Yakin ingin menghapus booking ini?')) return;
                            if (!gor || !bookingInfo.tanggal || !editDialog) return;
                            setEditLoading(true);
                            try {
                                // Gunakan ID Booking untuk identifikasi yang lebih akurat
                                const playerDetail = bookingInfo.daftarPemainDetail?.find(p => p.nama === editDialog.player && p.email === editDialog.playerEmail) ||
                                                   bookingInfo.daftarWaitinglistDetail?.find(p => p.nama === editDialog.player && p.email === editDialog.playerEmail);
                                
                                if (!playerDetail?.idBooking) {
                                    alert('ID Booking tidak ditemukan. Tidak dapat menghapus booking.');
                                    return;
                                }
                                
                                await axios.post('/api/coaching?action=cancel', {
                                  idBooking: playerDetail.idBooking
                                });
                                setEditDialog(null);
                                fetchBookingNames();
                                fetchGorData();
                            } catch (e) {
                                alert('Gagal menghapus booking. Pastikan Anda punya akses.');
                            } finally {
                                setEditLoading(false);
                            }
                        }}
                        disabled={editLoading}
                    >
                        Hapus
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        disabled={editLoading}
                    >
                        {editLoading ? 'Menyimpan...' : 'Simpan'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Dialog */}
            <Dialog
                open={showShareDialog}
                onClose={() => setShowShareDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        backgroundColor: 'background.paper'
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1
                }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                        📤 Bagikan Info Booking
                    </Typography>
                    <IconButton onClick={() => setShowShareDialog(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Pilih platform untuk membagikan info booking terkini:
                    </Typography>

                    <List sx={{ pt: 0 }}>
                        <ListItem
                            button
                            onClick={shareToWhatsApp}
                            sx={{
                                borderRadius: 2,
                                mb: 1,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <ListItemIcon>
                                <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="WhatsApp"
                                secondary="Bagikan ke WhatsApp"
                            />
                        </ListItem>

                        <ListItem
                            button
                            onClick={shareToTwitter}
                            sx={{
                                borderRadius: 2,
                                mb: 1,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <ListItemIcon>
                                <TwitterIcon sx={{ color: '#1DA1F2', fontSize: 28 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Twitter"
                                secondary="Tweet info booking"
                            />
                        </ListItem>

                        <ListItem
                            button
                            onClick={shareToFacebook}
                            sx={{
                                borderRadius: 2,
                                mb: 1,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <ListItemIcon>
                                <FacebookIcon sx={{ color: '#1877F2', fontSize: 28 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Facebook"
                                secondary="Post ke Facebook"
                            />
                        </ListItem>

                        <ListItem
                            button
                            onClick={shareToTelegram}
                            sx={{
                                borderRadius: 2,
                                mb: 1,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <ListItemIcon>
                                <TelegramIcon sx={{ color: '#0088cc', fontSize: 28 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Telegram"
                                secondary="Bagikan ke Telegram"
                            />
                        </ListItem>

                        <ListItem
                            button
                            onClick={copyToClipboard}
                            sx={{
                                borderRadius: 2,
                                mb: 1,
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <ListItemIcon>
                                <ContentCopyIcon sx={{ fontSize: 28 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Salin ke Clipboard"
                                secondary="Salin teks untuk dibagikan"
                            />
                        </ListItem>
                    </List>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={shareSnackbarOpen}
                autoHideDuration={3000}
                onClose={() => setShareSnackbarOpen(false)}
                message="Info booking berhasil disalin ke clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{
                    sx: {
                        backgroundColor: '#4caf50',
                        color: '#fff',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 500
                    }
                }}
            />

            <Dialog
                open={showPaymentErrorDialog}
                onClose={() => setShowPaymentErrorDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { background: theme.palette.mode === 'dark' ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)` : undefined, borderRadius: 3 } }}
            >
                <DialogTitle sx={{ textAlign: 'center', color: 'error.main' }}>
                    ⚠️ Gagal Memproses Pembayaran
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                        Sistem pembayaran mengalami gangguan sementara.
                        <br />
                        Silakan coba lagi atau hubungi admin jika masalah berlanjut.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRetryPayment}
                            startIcon={<RefreshIcon />}
                        >
                            Coba Lagi
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            href="https://wa.me/+6283890485939"
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<WhatsAppIcon />}
                        >
                            Hubungi Admin
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CoachingClinicBooking;
